import React from 'react';
import { useTheme } from '@/context/ThemeContext';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { TYPEFORM_URL } from '@/data/mock';
import { useCountUp } from '@/hooks/useCountUp';

export function SectionHeading({ heading, subtitle, centered = true }) {
  const { currentTheme: t } = useTheme();
  return (
    <div className={`mb-12 lg:mb-16 ${centered ? 'text-center max-w-3xl mx-auto' : ''}`}>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-3xl lg:text-4xl font-bold mb-4"
        style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}
      >
        {heading}
      </motion.h2>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-base lg:text-lg leading-relaxed"
          style={{ color: t.textSecondary, fontFamily: 'Inter, sans-serif' }}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

export function FeatureCard({ icon: Icon, title, description, index = 0 }) {
  const { currentTheme: t, theme } = useTheme();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="p-6 rounded-xl border transition-all duration-300 group"
      style={{
        background: t.cardBg,
        borderColor: t.cardBorder,
        backdropFilter: theme === 'aurora' ? 'blur(10px)' : 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 12px 32px ${t.shadowHover}`;
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: t.featureIconBg }}
      >
        {Icon && <Icon size={22} style={{ color: t.featureIconColor }} />}
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: t.textSecondary, fontFamily: 'Inter, sans-serif' }}>{description}</p>
    </motion.div>
  );
}

export function StatCard({ stat, description, index = 0 }) {
  const { currentTheme: t, theme } = useTheme();
  const { ref, formattedCount } = useCountUp(stat, 2000);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="text-center p-6 rounded-xl border"
      style={{
        background: t.statBg,
        borderColor: t.borderColor,
        backdropFilter: theme === 'aurora' ? 'blur(10px)' : 'none',
      }}
    >
      <div className="text-3xl lg:text-4xl font-extrabold mb-2" style={{ color: t.accent, fontFamily: 'Manrope, sans-serif' }}>
        {formattedCount}
      </div>
      <p className="text-sm" style={{ color: t.textSecondary, fontFamily: 'Inter, sans-serif' }}>{description}</p>
    </motion.div>
  );
}

export function AudienceCard({ icon: Icon, title, description, index = 0 }) {
  const { currentTheme: t, theme } = useTheme();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="p-6 rounded-xl border"
      style={{
        background: t.cardBg,
        borderColor: t.cardBorder,
        backdropFilter: theme === 'aurora' ? 'blur(10px)' : 'none',
      }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: t.featureIconBg }}
      >
        {Icon && <Icon size={22} style={{ color: t.featureIconColor }} />}
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: t.textSecondary, fontFamily: 'Inter, sans-serif' }}>{description}</p>
    </motion.div>
  );
}

export function CTAButton({ text = 'Solicitar demo', href, variant = 'primary', onClick }) {
  const { currentTheme: t } = useTheme();
  const isPrimary = variant === 'primary';

  const style = isPrimary
    ? { background: t.accent, color: '#FFFFFF' }
    : { background: 'transparent', color: t.text, border: `2px solid ${t.borderColor}` };

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
        style={style}
        onMouseEnter={(e) => {
          if (isPrimary) e.currentTarget.style.background = t.accentHover;
          else e.currentTarget.style.borderColor = t.accent;
        }}
        onMouseLeave={(e) => {
          if (isPrimary) e.currentTarget.style.background = t.accent;
          else e.currentTarget.style.borderColor = t.borderColor;
        }}
      >
        {text}
        {isPrimary && <ArrowRight size={16} />}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
      style={style}
      onMouseEnter={(e) => {
        if (isPrimary) e.currentTarget.style.background = t.accentHover;
        else e.currentTarget.style.borderColor = t.accent;
      }}
      onMouseLeave={(e) => {
        if (isPrimary) e.currentTarget.style.background = t.accent;
        else e.currentTarget.style.borderColor = t.borderColor;
      }}
    >
      {text}
      {isPrimary && <ArrowRight size={16} />}
    </button>
  );
}

export function VideoEmbed({ url, title }) {
  return (
    <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
      <iframe
        src={url}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
        style={{ border: 0 }}
      />
    </div>
  );
}

export function SolutionPageLayout({ heroData, features, stats, audiences, featureIcons, audienceIcons }) {
  const { currentTheme: t, theme } = useTheme();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-24" style={{ background: t.heroBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}
              >
                {heroData.heading}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-lg mb-8 leading-relaxed"
                style={{ color: t.textSecondary }}
              >
                {heroData.subtitle}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CTAButton href={TYPEFORM_URL} />
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              {heroData.videoUrl ? (
                <VideoEmbed url={heroData.videoUrl} title={heroData.heading} />
              ) : (
                <img
                  src={heroData.image}
                  alt={heroData.heading}
                  className="w-full rounded-xl shadow-2xl"
                  loading="lazy"
                />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            heading="Funcionalidades principales"
            subtitle="Todo lo que necesitas para transformar tu organización."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[f.icon];
              return <FeatureCard key={i} icon={Icon} title={f.title} description={f.description} index={i} />;
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-24" style={{ background: t.sectionAltBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="Impacto y resultados" subtitle="Resultados reales que transforman organizaciones." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => <StatCard key={i} {...s} index={i} />)}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="py-16 lg:py-24" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading heading="¿Para quién es?" subtitle="Industrias y problemáticas que resuelve esta solución." />
          <div className="grid md:grid-cols-3 gap-6">
            {audiences.map((a, i) => {
              const Icon = audienceIcons[a.icon];
              return <AudienceCard key={i} icon={Icon} title={a.title} description={a.description} index={i} />;
            })}
          </div>
          <div className="text-center mt-12">
            <CTAButton text="Agenda una demo personalizada" href={TYPEFORM_URL} />
          </div>
        </div>
      </section>
    </div>
  );
}
